import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';

import styles from './SubscriptionDowngrade.module.css';
import { fetchUserByToken } from '../../app/features/userSlice';
import {downgradeSubscription, upgradeSubscription, activeFreePlan } from '../../services/paymentService';
import HeroSection from '../../components/Verification/HeroSection/HeroSection';
import getPlanFeatures from './data/planFeatures';

// Import components
import PricingTabs from './components/PricingTabs';
import PlanDescription from './components/PlanDescription';
import PlanCard from './components/PlanCard';
import PlanFeatures from './components/PlanFeatures';
import CancellationSection from './components/CancellationSection';
import FaqSection from './components/FaqSection';

function SubscriptionDowngrade() {
    const [activeTab, setActiveTab] = useState('Monthly');
    const [userSubscriptionInterval, setUserSubscriptionInterval] = useState(null);
    const user = useSelector(state => state.user.user);
    const userToken = useSelector(state => state.user.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [loadingPlan, setLoadingPlan] = useState({
        sme: false,
        basic: false,
        free: false
    });

    useEffect(() => {
        // Check if user has a subscription
        if (!user?.subscriptionStatus || user?.subscriptionStatus !== 'active') {
            navigate('/profile/billing');
        }
        
        // Determine the user's subscription interval
        if (user?.activeSubscriptionPriceId) {
            const isAnnual = 
                user.activeSubscriptionPriceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_SME_FOR_1_YEAR ||
                user.activeSubscriptionPriceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_BASIC_FOR_1_YEAR ||
                user.activeSubscriptionPriceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_FREE_FOR_1_YEAR;
            
            const interval = isAnnual ? 'Annual' : 'Monthly';
            setUserSubscriptionInterval(interval);
            setActiveTab(interval); // Set the active tab to match the user's subscription interval
        }
    }, [user, navigate]);

    const handleTabChange = (tab) => {
        // Allow changing tabs freely
        setActiveTab(tab);
    };

    const handleUpgradeToSME = () => {
        setLoadingPlan(prev => ({ ...prev, sme: true }));
        const priceId = activeTab === 'Annual' 
            ? process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_SME_FOR_1_YEAR 
            : process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_SME_FOR_1_MONTH;
        
        // Determine if this is a monthly to annual upgrade of the same tier
        const isMonthlyToAnnualUpgrade = user?.activeSubscriptionPriceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_SME_FOR_1_MONTH && 
                                         activeTab === 'Annual' && 
                                         priceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_SME_FOR_1_YEAR;
        
        // Determine if this is an annual to monthly downgrade of the same tier
        const isAnnualToMonthlyDowngrade = user?.activeSubscriptionPriceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_SME_FOR_1_YEAR && 
                                         activeTab === 'Monthly' && 
                                         priceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_SME_FOR_1_MONTH;
        
        // Use upgradeSubscription for monthly to annual changes, downgradeSubscription for annual to monthly changes
        const apiCall = isAnnualToMonthlyDowngrade ? downgradeSubscription : upgradeSubscription;
        
        apiCall(userToken, priceId)
            .then(response => {
                dispatch(fetchUserByToken(userToken));
                setLoadingPlan(prev => ({ ...prev, sme: false }));
                // If payment confirmation needed (for free to paid upgrades)
                if (response.clientSecret) {
                    navigate('/profile/billing/payment-confirmation', { 
                        state: { 
                            clientSecret: response.clientSecret,
                            subscriptionId: response.subscriptionId,
                            priceId: priceId
                        } 
                    });
                } else {
                    // For paid to paid upgrades
                    navigate('/profile/billing');
                }
            })
            .catch(error => {
                console.error(error);
                setLoadingPlan(prev => ({ ...prev, sme: false }));
            });
    };

    const handleUpgradeToBasic = () => {
        setLoadingPlan(prev => ({ ...prev, basic: true }));
        const priceId = activeTab === 'Annual' 
            ? process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_BASIC_FOR_1_YEAR 
            : process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_BASIC_FOR_1_MONTH;
            
        // If user has monthly basic and is upgrading to annual basic, use upgradeSubscription instead of downgradeSubscription
        const isMonthlyToAnnualUpgrade = user?.activeSubscriptionPriceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_BASIC_FOR_1_MONTH && 
                                         activeTab === 'Annual' && 
                                         priceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_BASIC_FOR_1_YEAR;
        
        // Determine if this is an annual to monthly downgrade of the same tier
        const isAnnualToMonthlyDowngrade = user?.activeSubscriptionPriceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_BASIC_FOR_1_YEAR && 
                                         activeTab === 'Monthly' && 
                                         priceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_BASIC_FOR_1_MONTH;
        
        // Use upgradeSubscription for monthly to annual changes, downgradeSubscription for annual to monthly changes
        const apiCall = isAnnualToMonthlyDowngrade ? downgradeSubscription : upgradeSubscription;
        
        apiCall(userToken, priceId)
            .then(response => {
                dispatch(fetchUserByToken(userToken));
                setLoadingPlan(prev => ({ ...prev, basic: false }));
                // If payment confirmation needed (for free to paid upgrades)
                if (response.clientSecret) {
                    navigate('/profile/billing/payment-confirmation', { 
                        state: { 
                            clientSecret: response.clientSecret,
                            subscriptionId: response.subscriptionId,
                            priceId: priceId
                        } 
                    });
                } else {
                    // For paid to paid upgrades
                    navigate('/profile/billing');
                }
            })
            .catch(error => {
                console.error(error);
                setLoadingPlan(prev => ({ ...prev, basic: false }));
            });
    };

    const handleDowngradeToBasic = () => {
        setLoadingPlan(prev => ({ ...prev, basic: true }));
        const priceId = activeTab === 'Annual' 
            ? process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_BASIC_FOR_1_YEAR 
            : process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_BASIC_FOR_1_MONTH;
        
        // Determine if this is actually an upgrade (monthly to annual of same tier)
        const isMonthlyToAnnualUpgrade = user?.activeSubscriptionPriceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_BASIC_FOR_1_MONTH && 
                                         activeTab === 'Annual' && 
                                         priceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_BASIC_FOR_1_YEAR;
        
        // Determine if this is an annual to monthly downgrade of the same tier
        const isAnnualToMonthlyDowngrade = user?.activeSubscriptionPriceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_BASIC_FOR_1_YEAR && 
                                         activeTab === 'Monthly' && 
                                         priceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_BASIC_FOR_1_MONTH;
                                         
        // Use upgradeSubscription for monthly to annual changes, downgradeSubscription for others
        const apiCall = isMonthlyToAnnualUpgrade ? upgradeSubscription : 
                        isAnnualToMonthlyDowngrade ? downgradeSubscription : 
                        downgradeSubscription;
        
        apiCall(userToken, priceId)
            .then(response => {
                dispatch(fetchUserByToken(userToken));
                setLoadingPlan(prev => ({ ...prev, basic: false }));
                // If payment confirmation needed (for monthly to annual upgrades)
                if (response.clientSecret) {
                    navigate('/profile/billing/payment-confirmation', { 
                        state: { 
                            clientSecret: response.clientSecret,
                            subscriptionId: response.subscriptionId,
                            priceId: priceId
                        } 
                    });
                } else {
                    navigate('/profile/billing');
                }
            })
            .catch(error => {
                console.error(error);
                setLoadingPlan(prev => ({ ...prev, basic: false }));
            });
    };

    const handleDowngradeToFree = () => {
        setLoadingPlan(prev => ({ ...prev, free: true }));
        navigate('/discount-offer');
    };

    // Get plan features from the external file
    const planFeatures = getPlanFeatures(activeTab, user);
    
    // Set button actions based on button text
    if (planFeatures.sme.buttonText === t('subscription.upgrade')) {
        planFeatures.sme.buttonAction = handleUpgradeToSME;
    } else if (planFeatures.sme.buttonText === t('subscription.downgrade')) {
        planFeatures.sme.buttonAction = handleUpgradeToSME; // Use the same function for downgrades
    }
    
    if (planFeatures.basic.buttonText === t('subscription.upgrade')) {
        planFeatures.basic.buttonAction = handleUpgradeToBasic;
    } else if (planFeatures.basic.buttonText === t('subscription.downgrade')) {
        planFeatures.basic.buttonAction = handleDowngradeToBasic;
    }
    
    if (planFeatures.free.buttonText === t('subscription.downgrade')) {
        planFeatures.free.buttonAction = handleDowngradeToFree;
    }

    return (
        <div className={styles.downgradePage}>
            <Helmet>
                <title>{t('subscription.page_title')}</title>
            </Helmet>

            <div className={styles.HeroSectionWrapper}>
                <HeroSection
                    title={t('subscription.hero_title')}
                    desc={t('subscription.hero_description')}
                    withOutVerification={true}
                />
            </div>

            <div className={styles.pricingSection}>
                <p className={styles.heading}>{t('subscription.pricing_plans')}</p>
                
                <PricingTabs 
                    activeTab={activeTab} 
                    handleTabChange={handleTabChange}
                    userSubscriptionInterval={userSubscriptionInterval}
                />
                
                <PlanDescription />

                <div className={styles.planCards}>
                    <PlanCard plan={planFeatures.sme} loading={loadingPlan.sme} />
                    <PlanCard plan={planFeatures.basic} loading={loadingPlan.basic} />
                    <PlanCard plan={planFeatures.free} loading={loadingPlan.free} />
                </div>
            </div>
            
            <PlanFeatures />
            
            <CancellationSection 
                handleCancelSubscription={ ()=>{}} 
                loading={false} 
                to={'/discount-offer'}
            />
            
            <FaqSection />
        </div>
    );
}

export default SubscriptionDowngrade; 